import axios, { HttpStatusCode, isAxiosError, isCancel } from 'axios';
import { useCallback, useState } from 'react';

import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';

const useUrlValidator = () => {

    const [abortController, setAbortController] = useState(new AbortController());

    const validateUrlAsync = useCallback(async (url: string): Promise<string> => {

        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return '';
        }

        // Abort any previous check requests
        abortController.abort();

        // Track the next request in case we need to abort later on
        const newAbortController = new AbortController();
        setAbortController(newAbortController);

        const validateEndpoint = `${restUrl('getUrlInfo')}?url=${encodeURIComponent(trimmedUrl)}`;

        try {
            const response = await axios.get(validateEndpoint, {
                signal: newAbortController.signal
            });

            if (response.status === HttpStatusCode.Ok) {
                return response.data.contentType ?? '';
            }

            logger.debug('useUrlValidator: Could not validate url', response.status);
            return '';
        } catch (error) {
            // Expected when the URL changes and the previous request is aborted
            if (isCancel(error)) {
                throw error;
            }

            // Unreachable / network failures are expected for bad Image/Document URLs
            if (isAxiosError(error)) {
                logger.debug('useUrlValidator: getUrlInfo failed', error.response?.status ?? error.message);
                return '';
            }

            logger.debug('useUrlValidator: Unexpected error validating url', error);
            return '';
        }

        // We only need one instance of this function.
        // eslint-disable-next-line
    }, []);

    return { validateUrlAsync };

};

export { useUrlValidator };
